import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';

const AdminTeamPage: React.FC = () => {
  const teamMembers = useAdminStore((s) => s.teamMembers);
  const deleteTeamMember = useAdminStore((s) => s.deleteTeamMember);
  const reorderTeamMembers = useAdminStore((s) => s.reorderTeamMembers);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const sorted = [...teamMembers].sort((a, b) => a.order - b.order);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteTeamMember(deleteId);
    toast.success('Team member deleted');
    setDeleteId(null);
  };

  const handleDragStart = useCallback((e: React.DragEvent, id: string): void => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) { setDragOverId(id); }
  }, [draggedId]);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string): void => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (sourceId && sourceId !== targetId) {
      const currentOrder = sorted.map((m) => m._id);
      const sourceIdx = currentOrder.indexOf(sourceId);
      const targetIdx = currentOrder.indexOf(targetId);
      if (sourceIdx !== -1 && targetIdx !== -1) {
        currentOrder.splice(sourceIdx, 1);
        currentOrder.splice(targetIdx, 0, sourceId);
        reorderTeamMembers(currentOrder);
        toast.success('Order updated');
      }
    }
    setDraggedId(null);
    setDragOverId(null);
  }, [sorted, reorderTeamMembers]);

  const handleDragEnd = useCallback((): void => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Team</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}</p>
        </div>
        <Link to="/admin/team/new" className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Plus size={14} /> Add Member
        </Link>
      </div>

      <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl divide-y divide-black/[0.03] dark:divide-white/[0.03] overflow-hidden">
        <AnimatePresence mode="popLayout">
          {sorted.map((member) => (
            <motion.div
              key={member._id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              draggable
              onDragStart={(e) => handleDragStart(e as unknown as React.DragEvent, member._id)}
              onDragOver={(e) => handleDragOver(e as unknown as React.DragEvent, member._id)}
              onDrop={(e) => handleDrop(e as unknown as React.DragEvent, member._id)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-4 px-5 py-4 transition-all cursor-grab active:cursor-grabbing ${draggedId === member._id ? 'opacity-40' : ''} ${dragOverId === member._id ? 'bg-black/[0.03] dark:bg-white/[0.03]' : ''}`}
            >
              <div className="text-black/15 dark:text-white/15 shrink-0 hover:text-black/30 dark:hover:text-white/30 transition-colors">
                <GripVertical size={16} />
              </div>
              <div className="w-10 h-10 rounded-full overflow-hidden bg-black/[0.02] dark:bg-white/[0.02] shrink-0">
                <img src={member.photo} alt={member.name.en} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-bold truncate">{member.name.en}</p>
                <p className="text-[10px] text-black/40 dark:text-white/40 truncate">{member.role.en}</p>
              </div>
              <span className="text-[9px] font-bold text-black/15 dark:text-white/15 tabular-nums shrink-0">#{member.order}</span>
              <div className="flex items-center gap-1 shrink-0">
                <Link to={`/admin/team/${member._id}`} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all">
                  <Pencil size={12} />
                </Link>
                <button onClick={() => setDeleteId(member._id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all">
                  <Trash2 size={12} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {teamMembers.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">No team members found</p>
        </div>
      )}

      <AdminConfirmDialog isOpen={deleteId !== null} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Team Member" message="This will permanently remove this team member. This action cannot be undone." />
    </div>
  );
};

export default AdminTeamPage;
